import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';

import {
  listCirclesByOrg,
  listCircleLevelByOrg,
  listUsersByOrg,
  listCircleMembers,
} from 'graphql/queries';
import { API } from 'aws-amplify';
import CSMetrics from './components/CSMetrics';
import CSTrends from './components/CSTrends';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cs-tabpanel-${index}`}
      aria-labelledby={`cs-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function CSDashboard({ orgsList }: { orgsList: any }) {
  const [organization, setOrganization] = useState('all');
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [teams, setTeams] = useState('all');
  const [individuals, setIndividuals] = useState('all');
  const [granularity, setGranularity] = useState<
    'daily' | 'weekly' | 'monthly'
  >('monthly');
  const [teamsList, setTeamsList] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [individualsList, setIndividualsList] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [individualsLoading, setIndividualsLoading] = useState(false);
  const [circleMembers, setCircleMembers] = useState<any[]>([]);
  const [originalIndividualsList, setOriginalIndividualsList] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [activeTab, setActiveTab] = useState(0);
  const [showDateWarning, setShowDateWarning] = useState(false);

  // Trends-specific date state
  const [trendsYear, setTrendsYear] = useState<number>(
    new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).getFullYear()
  );
  const [trendsMonth, setTrendsMonth] = useState<number>(new Date().getMonth());
  const [trendsWeek, setTrendsWeek] = useState<number>(1);
  const [trendsStartMonth, setTrendsStartMonth] = useState<number>(
    new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).getMonth()
  );

  // Fetch teams when organization changes
  // This function first gets the highest level number for the organization,
  // then fetches only the teams (circles) that match that level ID
  const fetchTeams = async (orgId: string) => {
    if (orgId === 'all') {
      setTeamsList([]);
      setTeams('all');
      return;
    }

    setTeamsLoading(true);

    try {
      // First, get the highest level number for this organization

      const levelQueryVariables = {
        orgID: orgId,
        filter: {
          isDeleted: { eq: false },
        },
      };

      const levelResponse: any = await API.graphql({
        query: listCircleLevelByOrg,
        variables: levelQueryVariables,
      });

      // Check for GraphQL errors
      if (levelResponse.errors && levelResponse.errors.length > 0) {
        console.error(
          'fetchTeams: GraphQL errors in level response:',
          levelResponse.errors
        );
        throw new Error(
          `GraphQL errors: ${levelResponse.errors
            .map((e: any) => e.message)
            .join(', ')}`
        );
      }

      // Check if data is null (which can happen with GraphQL errors)
      if (!levelResponse.data || !levelResponse.data.listCircleLevelByOrg) {
        console.error('fetchTeams: No data returned from listCircleLevelByOrg');
        throw new Error('No data returned from listCircleLevelByOrg query');
      }

      const levels = levelResponse.data.listCircleLevelByOrg.items || [];

      if (levels.length === 0) {
        setTeamsList([]);
        setTeams('all');
        return;
      }

      // Sort levels by levelNum in descending order (highest first)
      const sortedLevels = levels.sort(
        (a: any, b: any) => b.levelNum - a.levelNum
      );

      // Find the level with the highest levelNum (first item after sorting)
      const highestLevel = sortedLevels[0];

      // Ensure we have a valid level ID
      if (!highestLevel || !highestLevel.id) {
        console.error('fetchTeams: Invalid highest level data:', highestLevel);
        setTeamsList([]);
        setTeams('all');
        return;
      }

      const teamsQueryVariables = {
        orgID: orgId,
        filter: {
          isDeleted: { eq: false },
          isActive: { eq: true },
          levelID: { eq: highestLevel.id },
        },
      };

      const teamsResponse: any = await API.graphql({
        query: listCirclesByOrg,
        variables: teamsQueryVariables,
      });

      // Check for GraphQL errors
      if (teamsResponse.errors && teamsResponse.errors.length > 0) {
        console.error(
          'fetchTeams: GraphQL errors in teams response:',
          teamsResponse.errors
        );
        throw new Error(
          `GraphQL errors: ${teamsResponse.errors
            .map((e: any) => e.message)
            .join(', ')}`
        );
      }

      // Check if data is null (which can happen with GraphQL errors)
      if (!teamsResponse.data || !teamsResponse.data.listCirclesByOrg) {
        console.error('fetchTeams: No data returned from listCirclesByOrg');
        throw new Error('No data returned from listCirclesByOrg query');
      }

      const teamsData = teamsResponse.data.listCirclesByOrg.items || [];

      if (teamsData.length === 0) {
        setTeamsList([]);
        setTeams('all');
        return;
      }

      const formattedTeams = teamsData.map((team: any) => ({
        id: team.id,
        name: team.name,
      }));

      setTeamsList(formattedTeams);
      // Reset teams selection to 'all' when organization changes
      setTeams('all');
    } catch (error: any) {
      console.error('fetchTeams: Error occurred:', error);

      // Try to extract meaningful error information
      let errorMessage = 'Unknown error occurred';
      let errorDetails = {};

      if (error.message) {
        errorMessage = error.message;
      } else if (error.errors && Array.isArray(error.errors)) {
        errorMessage = `GraphQL errors: ${error.errors
          .map((e: any) => e.message || 'Unknown error')
          .join(', ')}`;
        errorDetails = { graphqlErrors: error.errors };
      } else if (error.data === null) {
        errorMessage = 'GraphQL query returned null data';
        errorDetails = { responseData: error.data };
      }

      console.error('fetchTeams: Error details:', {
        message: errorMessage,
        stack: error.stack,
        orgId,
        ...errorDetails,
      });

      setTeamsList([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const fetchIndividuals = async (
    orgId: string,
    shouldResetList: boolean = true
  ) => {
    if (orgId === 'all') {
      setIndividualsList([]);
      setIndividuals('all');
      return;
    }

    setIndividualsLoading(true);
    try {
      const usersQueryVariables = {
        orgID: orgId,
        filter: {
          isDeleted: { eq: false },
          status: { ne: 'deleted' },
        },
        limit: 1000,
      };

      const usersResponse: any = await API.graphql({
        query: listUsersByOrg,
        variables: usersQueryVariables,
      });

      // Check for GraphQL errors
      if (usersResponse.errors && usersResponse.errors.length > 0) {
        console.error(
          'fetchIndividuals: GraphQL errors in users response:',
          usersResponse.errors
        );
        throw new Error(
          `GraphQL errors: ${usersResponse.errors
            .map((e: any) => e.message)
            .join(', ')}`
        );
      }

      // Check if data is null (which can happen with GraphQL errors)
      if (!usersResponse.data || !usersResponse.data.listUsersByOrg) {
        console.error('fetchIndividuals: No data returned from listUsersByOrg');
        throw new Error('No data returned from listUsersByOrg query');
      }

      const usersData = usersResponse.data.listUsersByOrg.items || [];

      if (usersData.length === 0) {
        setIndividualsList([]);
        setIndividuals('all');
        return;
      }

      // Filter out users without names and format the data
      const formattedUsers = usersData
        .filter((user: any) => user.name && user.name.trim() !== '')
        .map((user: any) => ({
          id: user.id,
          name: user.name,
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));

      // Store the original list for filtering
      setOriginalIndividualsList(formattedUsers);
      setIndividualsList(formattedUsers);
      // Reset individuals selection to 'all' when organization changes
      if (shouldResetList) {
        setIndividuals('all');
      }
    } catch (error: any) {
      console.error('fetchIndividuals: Error occurred:', error);

      // Try to extract meaningful error information
      let errorMessage = 'Unknown error occurred';
      let errorDetails = {};

      if (error.message) {
        errorMessage = error.message;
      } else if (error.errors && Array.isArray(error.errors)) {
        errorMessage = `GraphQL errors: ${error.errors
          .map((e: any) => e.message || 'Unknown error')
          .join(', ')}`;
        errorDetails = { graphqlErrors: error.errors };
      } else if (error.data === null) {
        errorMessage = 'GraphQL query returned null data';
        errorDetails = { responseData: error.data };
      }

      console.error('fetchIndividuals: Error details:', {
        message: errorMessage,
        stack: error.stack,
        orgId,
        ...errorDetails,
      });

      setIndividualsList([]);
      setIndividuals('all');
    } finally {
      setIndividualsLoading(false);
    }
  };

  // Filter individuals by selected team (circleId)
  const filterIndividualsByTeam = (members: any[], selectedTeamId: string) => {
    if (selectedTeamId === 'all') {
      // If no team selected, show all individuals from the organization
      if (organization !== 'all' && originalIndividualsList.length > 0) {
        setIndividualsList(originalIndividualsList);
        setIndividuals('all');
      }
      return;
    }

    // Set loading state and immediately clear current list to prevent showing wrong data
    setIndividualsLoading(true);
    setIndividualsList([]);

    // If we don't have the original individuals list yet, we can't filter
    if (originalIndividualsList.length === 0) {
      console.warn(
        'Cannot filter individuals: originalIndividualsList is empty'
      );
      setIndividualsLoading(false);
      return;
    }

    // Filter circle members by the selected team
    const teamMembers = members.filter(
      (member) =>
        member.circleID === selectedTeamId &&
        member.isActive &&
        !member.isDeleted
    );

    // Get the user IDs from the team members
    const teamUserIds = teamMembers.map((member) => member.userID);

    // Filter the individuals list to only show team members
    const filteredIndividuals = originalIndividualsList.filter((individual) =>
      teamUserIds.includes(individual.id)
    );

    // Update the individuals list to show only team members
    setIndividualsList(filteredIndividuals);

    // Reset individuals selection to 'all' since the list changed
    setIndividuals('all');

    // Clear loading state
    setIndividualsLoading(false);
  };

  // Handle granularity change and adjust date range accordingly
  const handleGranularityChange = (
    newGranularity: 'daily' | 'weekly' | 'monthly'
  ) => {
    setGranularity(newGranularity);

    // Update trends date state based on new granularity
    if (activeTab === 1) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();

      switch (newGranularity) {
        case 'daily':
          setTrendsYear(currentYear);
          setTrendsMonth(currentMonth);
          setTrendsWeek(1);
          break;
        case 'weekly':
          setTrendsYear(currentYear);
          setTrendsMonth(currentMonth);
          break;
        case 'monthly':
          setTrendsYear(currentYear);
          setTrendsStartMonth(currentMonth);
          break;
      }
    }

    const end = new Date();
    let start: Date;

    switch (newGranularity) {
      case 'daily':
        // Daily: one week
        start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'weekly':
        // Weekly: one month
        start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        // Monthly: 3 months to 1 year
        start = new Date(end.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  // Convert trends date selections to actual start and end dates
  const getTrendsDateRange = () => {
    // Only calculate trends dates when the Trends tab is active
    if (activeTab !== 1) {
      return { startDate, endDate };
    }

    const year = trendsYear;

    switch (granularity) {
      case 'daily':
        const month = trendsMonth;
        const week = trendsWeek;
        // Calculate start of the week (Monday to Sunday)
        const monthStart = new Date(year, month, 1);
        const currentDate = new Date(monthStart);

        // Adjust to Monday if not already Monday
        const dayOfWeek = currentDate.getDay();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const adjustedDate = new Date(
          currentDate.getTime() - mondayOffset * 24 * 60 * 60 * 1000
        );

        // Move to the selected week
        const startOfWeek = new Date(
          adjustedDate.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000
        );
        const endOfWeek = new Date(
          startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000
        );
        return { startDate: startOfWeek, endDate: endOfWeek };

      case 'weekly':
        const monthForWeek = trendsMonth;
        const yearForWeek = year;

        console.log(
          'Weekly granularity - trendsMonth:',
          monthForWeek,
          'trendsYear:',
          yearForWeek
        );

        // Calculate start of the month
        const startOfMonthForWeek = new Date(yearForWeek, monthForWeek, 1);

        // Adjust to Monday if not already Monday (may include days from previous month)
        const dayOfWeekForWeek = startOfMonthForWeek.getDay();
        const mondayOffsetForWeek =
          dayOfWeekForWeek === 0 ? 6 : dayOfWeekForWeek - 1;
        const adjustedDateForWeek = new Date(
          startOfMonthForWeek.getTime() -
            mondayOffsetForWeek * 24 * 60 * 60 * 1000
        );

        // Calculate end date to cover all 5 weeks (may include days from next month)
        // Start from Monday, add 5 weeks to get to Monday of week 6, then subtract 1 day to get Sunday of week 5
        const endDateForWeek = new Date(
          adjustedDateForWeek.getTime() + (5 * 7 - 1) * 24 * 60 * 60 * 1000
        );

        console.log(
          'Weekly granularity - startDate:',
          adjustedDateForWeek,
          'endDate:',
          endDateForWeek
        );

        return { startDate: adjustedDateForWeek, endDate: endDateForWeek };

      case 'monthly':
        const startMonth = trendsStartMonth;
        const startYear = year;

        // Calculate end month and year (12 months from start)
        let endMonth = startMonth + 11;
        let endYear = startYear;

        // If end month exceeds December (11), adjust to next year
        if (endMonth > 11) {
          endMonth = endMonth - 12;
          endYear = startYear + 1;
        }

        const trendsStartDate = new Date(startYear, startMonth, 1);
        const trendsEndDate = new Date(endYear, endMonth + 1, 0); // Last day of end month
        return { startDate: trendsStartDate, endDate: trendsEndDate };

      default:
        return { startDate, endDate };
    }
  };

  // Handle date range changes and validate against granularity constraints
  const handleDateRangeChange = (
    newStartDate: Date | null,
    newEndDate: Date | null
  ) => {
    if (!newStartDate || !newEndDate) return;

    const timeDiff = newEndDate.getTime() - newStartDate.getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const oneWeekMs = 7 * oneDayMs;
    const oneMonthMs = 30 * oneDayMs;
    const oneYearMs = 365 * oneDayMs;

    if (timeDiff < oneMonthMs) {
      setShowDateWarning(true);
    } else {
      setShowDateWarning(false);
    }

    let isValidRange = true;
    let adjustedStartDate = newStartDate;
    const adjustedEndDate = newEndDate;

    switch (granularity) {
      case 'daily':
        if (timeDiff > oneWeekMs) {
          adjustedStartDate = new Date(newEndDate.getTime() - oneWeekMs);
          isValidRange = false;
        }
        break;
      case 'weekly':
        if (timeDiff > 5 * oneWeekMs) {
          adjustedStartDate = new Date(newEndDate.getTime() - 5 * oneWeekMs);
          isValidRange = false;
        }
        break;
      case 'monthly':
        if (timeDiff > oneYearMs) {
          adjustedStartDate = new Date(newEndDate.getTime() - oneYearMs);
          isValidRange = false;
        }
        break;
    }

    if (!isValidRange) {
      console.warn(`Date range adjusted for ${granularity} granularity`);
    }

    setStartDate(adjustedStartDate);
    setEndDate(adjustedEndDate);
  };

  useEffect(() => {
    fetchTeams(organization);
    fetchIndividuals(organization, true);
  }, [organization]);

  // Immediately clear individuals list when team changes to prevent showing wrong data
  // This prevents the race condition where users see unfiltered individuals briefly
  useEffect(() => {
    if (teams !== 'all') {
      // Clear individuals list immediately when team is selected
      setIndividualsList([]);
      setIndividuals('all');
    }
  }, [teams]);

  // Filter individuals when team selection changes or when data becomes available
  useEffect(() => {
    if (teams === 'all') {
      // If no team selected, show all individuals from the organization
      if (organization !== 'all' && originalIndividualsList.length > 0) {
        setIndividualsList(originalIndividualsList);
        setIndividuals('all');
      }
    } else if (
      teams !== 'all' &&
      circleMembers.length > 0 &&
      originalIndividualsList.length > 0
    ) {
      // If team is selected and we have both circle members and individuals, filter immediately
      filterIndividualsByTeam(circleMembers, teams);
    }
  }, [teams, circleMembers, organization, originalIndividualsList]);

  // Call listCircleMembers with selected orgId filter
  useEffect(() => {
    const fetchCircleMembers = async () => {
      if (organization && organization !== 'all') {
        try {
          let allCircleMembers: any[] = [];
          let nextToken: string | null = null;
          let hasMore = true;

          while (hasMore) {
            const response: any = await API.graphql({
              query: listCircleMembers,
              variables: {
                filter: {
                  orgID: { eq: organization },
                },
                limit: 1000,
                nextToken,
              },
            });

            const circleMembersData = response.data?.listCircleMembers;
            const items = circleMembersData?.items || [];
            nextToken = circleMembersData?.nextToken || null;

            allCircleMembers = [...allCircleMembers, ...items];
            hasMore = !!nextToken;
          }

          // Store circle members in state for filtering
          setCircleMembers(allCircleMembers);

          // Don't filter here - let the useEffect handle it after individuals are loaded
        } catch (error) {
          console.error('Error fetching circle members:', error);
        }
      }
    };

    fetchCircleMembers();
  }, [organization]);

  const allOrgsOption = { id: 'all', name: 'All Organizations' };
  const finalOrgsList = [
    allOrgsOption,
    ...orgsList.map((org: any) => ({ id: org.id, name: org.id })),
  ];

  // Memoize the individuals options to prevent unnecessary re-renders
  const individualsOptions = useMemo(
    () => [{ id: 'all', name: 'All Individuals' }, ...individualsList],
    [individualsList]
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box mb={4}>
          <Typography
            variant="h4"
            fontWeight="bold"
            color="primary"
            gutterBottom
          >
            Customer Success Dashboard: {organization}
            {' ('}
            {startDate?.toLocaleDateString?.() || ''}-
            {endDate?.toLocaleDateString?.() || ''}
            {')'}
          </Typography>

          <Typography variant="subtitle1" color="text.secondary">
            Monitor key metrics and engagement across your organization
          </Typography>
        </Box>

        {showDateWarning && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            onClose={() => setShowDateWarning(false)}
          >
            Date range is less than 30 days. Login trends chart requires at
            least 1 month of data to display.
          </Alert>
        )}

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="CS Dashboard tabs"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '1rem',
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Metrics" />
            <Tab label="Trends" />
          </Tabs>
        </Paper>

        {/* Filters */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            {/* Organization Filter */}
            <Grid item xs={12} sm={6} md={activeTab === 0 ? 2 : 3}>
              <FormControl fullWidth>
                <Autocomplete
                  options={finalOrgsList}
                  getOptionLabel={(option) => option.name}
                  value={
                    finalOrgsList.find((org) => org.id === organization) || null
                  }
                  onChange={(_, newValue) => {
                    setOrganization(newValue ? newValue.id : 'all');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Organization" />
                  )}
                  sx={{
                    '& .MuiAutocomplete-paper': (theme) => ({
                      backgroundColor:
                        theme.custom?.background2 ||
                        theme.palette.background.paper,
                      boxShadow: '0px 4px 24px 0px #ABABAB5E !important',
                      border: '1px solid #D0D2D8',
                      borderRadius: '8px',
                    }),
                  }}
                />
              </FormControl>
            </Grid>

            {/* Teams Filter */}
            <Grid item xs={12} sm={6} md={activeTab === 0 ? 2 : 3}>
              <FormControl fullWidth disabled={organization === 'all'}>
                <Autocomplete
                  disabled={organization === 'all'}
                  options={[{ id: 'all', name: 'All Teams' }, ...teamsList]}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={
                    teams === 'all'
                      ? { id: 'all', name: 'All Teams' }
                      : teamsList.find((team) => team.id === teams) || null
                  }
                  onChange={(_, newValue) => {
                    setTeams(newValue ? newValue.id : 'all');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Teams" />
                  )}
                  sx={{
                    '& .MuiAutocomplete-paper': (theme) => ({
                      backgroundColor:
                        theme.custom?.background2 ||
                        theme.palette.background.paper,
                      boxShadow: '0px 4px 24px 0px #ABABAB5E !important',
                      border: '1px solid #D0D2D8',
                      borderRadius: '8px',
                    }),
                  }}
                  loading={teamsLoading}
                  loadingText="Loading Teams..."
                  noOptionsText="No teams available"
                />
              </FormControl>
            </Grid>

            {/* Individuals Filter */}
            <Grid item xs={12} sm={6} md={activeTab === 0 ? 2 : 3}>
              <FormControl fullWidth disabled={organization === 'all'}>
                <Autocomplete
                  disabled={
                    organization === 'all' ||
                    (teams !== 'all' &&
                      individualsList.length === 0 &&
                      !individualsLoading)
                  }
                  options={individualsOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={
                    individuals === 'all'
                      ? { id: 'all', name: 'All Individuals' }
                      : individualsList.find((ind) => ind.id === individuals) ||
                        null
                  }
                  onChange={(_, newValue) => {
                    setIndividuals(newValue ? newValue.id : 'all');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Individuals" />
                  )}
                  sx={{
                    '& .MuiAutocomplete-paper': (theme) => ({
                      backgroundColor:
                        theme.custom?.background2 ||
                        theme.palette.background.paper,
                      boxShadow: '0px 4px 24px 0px #ABABAB5E !important',
                      border: '1px solid #D0D2D8',
                      borderRadius: '8px',
                    }),
                  }}
                  loading={individualsLoading}
                  loadingText="Loading Individuals..."
                  noOptionsText={
                    teams !== 'all' &&
                    individualsList.length === 0 &&
                    !individualsLoading
                      ? 'Loading team members...'
                      : 'No individuals available'
                  }
                  ListboxProps={{
                    style: { maxHeight: '300px' },
                  }}
                  open={individualsLoading ? false : undefined}
                />
              </FormControl>
            </Grid>

            {/* Granularity Dropdown - Only show in Trends tab */}
            {activeTab === 1 && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Granularity</InputLabel>
                  <Select
                    value={granularity}
                    onChange={(e) =>
                      handleGranularityChange(
                        e.target.value as 'daily' | 'weekly' | 'monthly'
                      )
                    }
                    label="Granularity"
                    MenuProps={{
                      PaperProps: {
                        sx: (theme) => ({
                          backgroundColor:
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                          boxShadow: '0px 4px 24px 0px #ABABAB5E !important',
                          border: '1px solid #D0D2D8',
                          borderRadius: '8px',
                        }),
                      },
                    }}
                  >
                    <MenuItem value="daily">Daily (1 week max)</MenuItem>
                    <MenuItem value="weekly">Weekly (1 month max)</MenuItem>
                    <MenuItem value="monthly">
                      Monthly (12 months from start month)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Quick Date Range Dropdown - Only show in Metrics tab */}
            {activeTab === 0 && (
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <Select
                    labelId="quick-date-range-label"
                    value=""
                    onChange={(event) => {
                      const value = event.target.value;
                      const end = new Date();
                      let start: Date;

                      switch (value) {
                        case 'week':
                          start = new Date(
                            end.getTime() - 7 * 24 * 60 * 60 * 1000
                          );
                          break;
                        case 'month':
                          start = new Date(
                            end.getTime() - 30 * 24 * 60 * 60 * 1000
                          );
                          break;
                        case 'year':
                          start = new Date(
                            end.getTime() - 365 * 24 * 60 * 60 * 1000
                          );
                          break;
                        default:
                          return;
                      }

                      handleDateRangeChange(start, end);
                    }}
                    displayEmpty
                    renderValue={() => 'Quick Range'}
                    sx={{
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: (theme) => ({
                          backgroundColor:
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                          border: '1px solid #D0D2D8',
                          borderRadius: '8px',
                          boxShadow: '0px 4px 24px 0px #ABABAB5E !important',
                        }),
                      },
                    }}
                  >
                    <MenuItem value="week">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.primary.main,
                          }}
                        />
                        Past Week
                      </Box>
                    </MenuItem>
                    <MenuItem value="month">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.secondary.main,
                          }}
                        />
                        Past Month
                      </Box>
                    </MenuItem>
                    <MenuItem value="year">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.success.main,
                          }}
                        />
                        Past Year
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Date Range Pickers - Only show in Metrics tab */}
            {activeTab === 0 && (
              <>
                <Grid item xs={12} sm={6} md={1.75}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    maxDate={new Date()}
                    onChange={(value) => {
                      if (value) {
                        handleDateRangeChange(value, endDate);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                    inputFormat="MM/dd/yyyy"
                    PopperProps={{
                      placement: 'bottom',
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            altAxis: true,
                            tether: false,
                          },
                        },
                        {
                          name: 'flip',
                          options: {
                            fallbackPlacements: [
                              'bottom',
                              'bottom-start',
                              'bottom-end',
                            ],
                          },
                        },
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1.75}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    maxDate={new Date()}
                    onChange={(value) => {
                      if (value) {
                        handleDateRangeChange(startDate, value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                    inputFormat="MM/dd/yyyy"
                    PopperProps={{
                      placement: 'bottom',
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            altAxis: true,
                            tether: false,
                          },
                        },
                        {
                          name: 'flip',
                          options: {
                            fallbackPlacements: [
                              'bottom',
                              'bottom-start',
                              'bottom-end',
                            ],
                          },
                        },
                      ],
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Date Controls for Trends - Only show when in Trends tab */}
        {activeTab === 1 && (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 4,
              mt: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: (theme) =>
                theme.custom?.background2 || theme.palette.background.paper,
            }}
          >
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              {/* Date Controls based on granularity */}
              {granularity === 'daily' && (
                <>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={trendsYear}
                        onChange={(e) =>
                          setTrendsYear(e.target.value as number)
                        }
                        label="Year"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {Array.from(
                          { length: 5 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={trendsMonth}
                        onChange={(e) =>
                          setTrendsMonth(e.target.value as number)
                        }
                        label="Month"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {[
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                        ].map((month, index) => (
                          <MenuItem key={index} value={index}>
                            {month}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Week</InputLabel>
                      <Select
                        value={trendsWeek}
                        onChange={(e) =>
                          setTrendsWeek(e.target.value as number)
                        }
                        label="Week"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {(() => {
                          const year = trendsYear;
                          const month = trendsMonth;
                          const startOfMonth = new Date(year, month, 1);

                          // Calculate weeks in the month (Monday to Sunday)
                          const weeks = [];
                          const initialDate = new Date(startOfMonth);

                          // Adjust to Monday if not already Monday
                          const dayOfWeek = initialDate.getDay();
                          const mondayOffset =
                            dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                          const adjustedDate = new Date(
                            initialDate.getTime() -
                              mondayOffset * 24 * 60 * 60 * 1000
                          );

                          let weekNum = 1;
                          let currentDate = adjustedDate;
                          while (weekNum <= 5) {
                            const weekStart = new Date(currentDate);
                            const weekEnd = new Date(currentDate);
                            weekEnd.setDate(weekEnd.getDate() + 6);

                            const weekStartStr = weekStart.toLocaleDateString(
                              'en-US',
                              { month: 'short', day: 'numeric' }
                            );
                            const weekEndStr = weekEnd.toLocaleDateString(
                              'en-US',
                              { month: 'short', day: 'numeric' }
                            );

                            weeks.push({
                              number: weekNum,
                              label: `Week ${weekNum}: ${weekStartStr} - ${weekEndStr}`,
                            });

                            currentDate = new Date(
                              currentDate.getTime() + 7 * 24 * 60 * 60 * 1000
                            );
                            weekNum++;
                          }

                          return weeks.map((week) => (
                            <MenuItem key={week.number} value={week.number}>
                              {week.label}
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {granularity === 'weekly' && (
                <>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={trendsYear}
                        onChange={(e) =>
                          setTrendsYear(e.target.value as number)
                        }
                        label="Year"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {Array.from(
                          { length: 5 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={trendsMonth}
                        onChange={(e) =>
                          setTrendsMonth(e.target.value as number)
                        }
                        label="Month"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {[
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                        ].map((month, index) => (
                          <MenuItem key={index} value={index}>
                            {month}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {granularity === 'monthly' && (
                <>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={trendsYear}
                        onChange={(e) =>
                          setTrendsYear(e.target.value as number)
                        }
                        label="Year"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {Array.from(
                          { length: 5 },
                          (_, i) => new Date().getFullYear() - i
                        ).map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Start Month</InputLabel>
                      <Select
                        value={trendsStartMonth}
                        onChange={(e) =>
                          setTrendsStartMonth(e.target.value as number)
                        }
                        label="Start Month"
                        sx={{
                          backgroundColor: (theme) =>
                            theme.custom?.background2 ||
                            theme.palette.background.paper,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: (theme) => ({
                              backgroundColor:
                                theme.custom?.background2 ||
                                theme.palette.background.paper,
                              boxShadow:
                                '0px 4px 24px 0px #ABABAB5E !important',
                              border: '1px solid #D0D2D8',
                              borderRadius: '8px',
                            }),
                          },
                        }}
                      >
                        {[
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                        ].map((month, index) => (
                          <MenuItem key={index} value={index}>
                            {month}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        )}

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <CSMetrics
            organization={organization}
            startDate={startDate}
            endDate={endDate}
            teams={teams}
            individuals={individuals}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {activeTab === 1 &&
            (() => {
              const trendsDates = getTrendsDateRange();
              console.log('CSTrends dates:', trendsDates);
              return (
                <CSTrends
                  organization={organization}
                  startDate={trendsDates.startDate}
                  endDate={trendsDates.endDate}
                  teams={teams}
                  individuals={individuals}
                  granularity={granularity}
                />
              );
            })()}
        </TabPanel>
      </Container>
    </LocalizationProvider>
  );
}
